import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Crown, Menu, X, Moon, Sun } from 'lucide-react';
import { cmsPublic } from '../../lib/cmsApi';
import { useTheme, useAuth } from '../../App';

/**
 * SiteShell — global chrome for the public marketing site.
 * Pulls top-level published pages from `/api/cms/pages` for nav,
 * renders the active children, and provides a footer.
 */
export default function SiteShell({ children }) {
  const [pages, setPages] = useState([]);
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { user, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    cmsPublic.listPages()
      .then(d => setPages((d?.items || []).filter(p => !p.parent_slug && p.nav_label)))
      .catch(() => setPages([]));
  }, []);

  // Close mobile nav on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);

  return (
    <div className="min-h-screen orka-surface-page flex flex-col" data-testid="site-shell">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[color:var(--orka-surface-page)]/80 border-b orka-border-subtle">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/site/home" className="flex items-center gap-2 orka-focus-ring rounded-md" data-testid="site-shell-logo">
            <Crown className="w-5 h-5 text-amber-500" />
            <span className="font-semibold text-base orka-text-primary tracking-tight">Orka</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="site-shell-nav">
            {pages.map(p => (
              <NavLink
                key={p.page_slug}
                to={`/site/${p.page_slug}`}
                className={({ isActive }) =>
                  `px-3 h-9 inline-flex items-center rounded-full text-sm orka-transition-colors ${
                    isActive
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'orka-text-secondary hover:orka-surface-subtle'
                  }`
                }
                data-testid={`site-shell-nav-${p.page_slug}`}
              >
                {p.nav_label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full orka-text-secondary hover:orka-surface-subtle orka-focus-ring orka-transition-colors"
              aria-label="Toggle theme"
              data-testid="site-shell-theme-toggle"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {user ? (
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="hidden md:inline-flex h-9 px-4 items-center rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium orka-transition-colors orka-focus-ring"
                data-testid="site-shell-cta-dashboard"
              >
                Open dashboard
              </button>
            ) : (
              <button
                type="button"
                onClick={() => login()}
                className="hidden md:inline-flex h-9 px-4 items-center rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium orka-transition-colors orka-focus-ring"
                data-testid="site-shell-cta-signin"
              >
                Sign in
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(v => !v)}
              className="md:hidden w-9 h-9 inline-flex items-center justify-center rounded-full orka-text-secondary hover:orka-surface-subtle orka-focus-ring"
              aria-label="Open menu"
              aria-expanded={open}
              data-testid="site-shell-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t orka-border-subtle" data-testid="site-shell-mobile-nav">
            <nav className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-1">
              {pages.map(p => (
                <Link
                  key={p.page_slug}
                  to={`/site/${p.page_slug}`}
                  className="h-10 px-3 inline-flex items-center rounded-lg text-sm orka-text-primary hover:orka-surface-subtle"
                >
                  {p.nav_label}
                </Link>
              ))}
              {user ? (
                <Link to="/dashboard" className="mt-2 h-10 px-3 inline-flex items-center justify-center rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium">
                  Open dashboard
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => login()}
                  className="mt-2 h-10 px-3 inline-flex items-center justify-center rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium"
                  data-testid="site-shell-mobile-cta-signin"
                >
                  Sign in
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1" data-testid="site-shell-main">{children}</main>

      <footer className="border-t orka-border-subtle py-10 mt-10" data-testid="site-shell-footer">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-amber-500" />
            <span className="font-semibold orka-text-primary">Orka</span>
            <span className="orka-text-tertiary text-sm">· The intelligence layer over your work</span>
          </div>
          <nav className="flex flex-wrap gap-4 text-sm orka-text-secondary">
            {pages.map(p => (
              <Link key={p.page_slug} to={`/site/${p.page_slug}`} className="hover:orka-text-primary">
                {p.nav_label}
              </Link>
            ))}
            <Link to="/site/privacy" className="hover:orka-text-primary">Privacy</Link>
            <Link to="/site/terms" className="hover:orka-text-primary">Terms</Link>
          </nav>
          <div className="orka-text-tertiary text-xs">© {new Date().getFullYear()} Orka</div>
        </div>
      </footer>
    </div>
  );
}
